import { useEffect, useState, useCallback, useRef } from 'react';
import { AuthenticatedConnectUser, paragon, SDK_EVENT } from '@useparagon/connect';

import { useAccountSelf } from '../../appHooks';
import config from 'App/babel/config';
import { ParagonConnectionMetadata } from './paragonTypes';
import logError from 'Helpers/logError';
import { useSelector, useAppDispatch } from 'App/components/hooks';
import { fetchParagonTokens } from 'Store/reducers/ExternalIntegrationReducer';

paragon.configureGlobal(
	{
		host: config.PARAGON.host
	},
	{
		WORKER_PROXY_PUBLIC_URL: config.PARAGON.workerProxyPublicUrl
	}
);

// Cache configuration for user authentication
const USER_CACHE_TTL = 30 * 1000; // 30 seconds cache TTL
interface UserCache {
	user: AuthenticatedConnectUser | null;
	timestamp: number;
	tokenHash: string;
}

const userCache: UserCache = {
	user: null,
	timestamp: 0,
	tokenHash: ''
};

const useParagonUser = () => {
	const accountSelf = useAccountSelf();
	const dispatch = useAppDispatch();
	const [user, setUser] = useState(paragon.getUser());
	const [error, setError] = useState();
	const tokens = useSelector(state => state.ExternalIntegration.tokens);
	const hasDispatchedTokenFetch = useRef(false);
	const isAuthenticatingRef = useRef(false);

	const createTokenHash = (tokens: any, accountSelf: any): string => {
		return JSON.stringify({
			paragonToken: tokens.paragonToken,
			upsalesToken: tokens.upsalesToken,
			projectId: tokens.projectId,
			clientId: accountSelf?.client?.id
		});
	};

	const isCacheValid = (currentTokenHash: string): boolean => {
		return (
			userCache.user !== null &&
			Date.now() - userCache.timestamp < USER_CACHE_TTL &&
			userCache.tokenHash === currentTokenHash
		);
	};

	const clearUserCache = () => {
		userCache.user = null;
		userCache.timestamp = 0;
		userCache.tokenHash = '';
	};

	const updateUser = useCallback(() => {
		const authedUser = paragon.getUser();
		if (authedUser.authenticated) {
			setUser({ ...authedUser });
			// Update cache when user state changes
			userCache.user = authedUser;
			userCache.timestamp = Date.now();
		}
	}, []);

	const setConnectionMetadataOnUserMeta = async (
		integrationType: string,
		configurationId: string,
		connectionMetadata: ParagonConnectionMetadata
	) => {
		const freshUser = paragon.getUser();
		if (!freshUser.authenticated || !connectionMetadata?.name) {
			return;
		}
		const integrationMetadata: any = freshUser.meta[integrationType] ?? {};
		if (!integrationMetadata[configurationId]) {
			integrationMetadata[configurationId] = {};
		}
		integrationMetadata[configurationId] = {
			...integrationMetadata[configurationId],
			...connectionMetadata
		};

		const updatedUser = await paragon.setUserMetadata({
			[integrationType]: integrationMetadata
		});
		setUser(updatedUser);
		// Update cache with new user data
		userCache.user = updatedUser;
		userCache.timestamp = Date.now();
	};

	const removeConnectionMetadataOnUserMeta = async (integrationType: string, configurationIds: string[]) => {
		const freshUser = paragon.getUser();
		if (!freshUser.authenticated) {
			return;
		}

		const integrationMetadata: any = freshUser.meta[integrationType] ?? {};
		for (const configurationId of configurationIds) {
			if (integrationMetadata[configurationId]) {
				delete integrationMetadata[configurationId];
			}
		}

		await paragon.setUserMetadata({
			[integrationType]: integrationMetadata
		});
		// Clear cache since user metadata changed
		clearUserCache();
	};

	const getProjectId = () => {
		// TODO: This will later be a setting in master, where we will be able to set which projects (multiple) the user should be able to use
		const paragonConfig = config.default ? config.default.PARAGON : config.PARAGON;
		if (!accountSelf) {
			return paragonConfig.projectId;
		}
		return paragonConfig.custom[accountSelf?.client.id] || paragonConfig.projectId;
	};

	// Listen for account state changes
	useEffect(() => {
		paragon.subscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, updateUser);
		paragon.subscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, updateUser);
		return () => {
			paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, updateUser);
			paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, updateUser);
		};
	}, []);

	useEffect(() => {
		// If tokens are not set, dispatch token fetch (but only once)
		if (!tokens.token || !tokens.upsalesToken) {
			if (!hasDispatchedTokenFetch.current) {
				hasDispatchedTokenFetch.current = true;
				dispatch(fetchParagonTokens());
			}
			return;
		}

		// Reset the flag when we have tokens
		hasDispatchedTokenFetch.current = false;

		if (tokens.token && tokens.upsalesToken && !error && accountSelf) {
			const currentTokenHash = createTokenHash(tokens, accountSelf);

			// Check if we have valid cached user data
			if (isCacheValid(currentTokenHash)) {
				setUser(userCache.user as AuthenticatedConnectUser);
				return;
			}

			// Prevent multiple concurrent authentication attempts
			if (isAuthenticatingRef.current) {
				return;
			}

			isAuthenticatingRef.current = true;
			const projectId = tokens.projectId || getProjectId();

			paragon
				.authenticate(projectId, tokens.token, {
					metadata: {
						name: accountSelf.client.id,
						upsalesToken: tokens.upsalesToken
					}
				})
				.then(() => {
					const authedUser = paragon.getUser();
					if (authedUser.authenticated) {
						setUser(authedUser);
						// Update cache with new user and token hash
						userCache.user = authedUser;
						userCache.timestamp = Date.now();
						userCache.tokenHash = currentTokenHash;
					}
				})
				.catch(e => {
					logError(e, 'Error when authenticating paragon user');
					setError(e);
					// Clear cache on authentication error
					clearUserCache();
				})
				.finally(() => {
					isAuthenticatingRef.current = false;
				});
		}
	}, [error, tokens, accountSelf]);

	return {
		user: user ? (user as AuthenticatedConnectUser) : null,
		setConnectionMetadataOnUserMeta,
		removeConnectionMetadataOnUserMeta
	};
};

export default useParagonUser;
